import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type TableColumn } from "react-data-table-component";
import { TablePager } from "../../components/shared/TablePager";
import { Dropdown } from "../../components/shared/Dropdown";
import { AdminGate } from "../../components/admin/AdminGate";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import { Modal } from "../../components/admin/Modal";
import { ErrorState, LoadingState } from "../../components/states";
import adminStyles from "../../components/admin/Admin.module.css";
import panel from "../../components/admin/AdminPanel.module.css";
import styles from "./AuditTrailPage.module.css";
import { fetchAuditActions, fetchAuditChainVerification, fetchAuditEntries, type AuditChainVerification, type AuditEntry, type AuditEntryPage } from "../../api/audit";
import { formatDateTime } from "../../components/consumption/formatters";

const ReadPermission = "audit.read";

/** The page sizes offered; the server allows up to 1000. */
const PageSizes = [25, 50, 100];

/** How long typing must pause before the search runs, so a search is not sent for every key. */
const SearchDelayMs = 300;

/** The filters: text the actor, subject or vendor contains, one recorded action (empty for any), and a period. */
interface Filters {
  search: string;
  action: string;
  from: string;
  to: string;
}

const NoFilters: Filters = { search: "", action: "", from: "", to: "" };

/** An action's recorded name (`PersonDistributionSet`) as words (`Person distribution set`). */
function actionLabel(action: string): string {
  const words = action.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[._-]+/g, " ").trim();
  return words.length === 0 ? action : words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/** Who did it: the person's name, or their email, or the recorded identifier; a system actor has none. */
function actorLabel(entry: AuditEntry, systemActor: string): string {
  return entry.actorName ?? entry.actorEmail ?? entry.actorId ?? systemActor;
}

/** What it was done to: a person by name or email, anything else by its kind and identifier. */
function subjectLabel(entry: AuditEntry): string {
  if (entry.subjectKind.toLowerCase() === "person") {
    return entry.subjectName ?? entry.subjectEmail ?? entry.subjectId;
  }
  return `${entry.subjectKind}: ${entry.subjectId}`;
}

/** A date input's value (`2026-10-27`) as the start of that day in UTC, for the search's inclusive `from`. */
function startOfDay(value: string): string | undefined {
  return value === "" ? undefined : `${value}T00:00:00Z`;
}

/** A date input's value as the start of the next day in UTC, for the search's exclusive `to`, so the day itself is included. */
function startOfNextDay(value: string): string | undefined {
  if (value === "") {
    return undefined;
  }
  const next = new Date(`${value}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString();
}

/**
 * The "Audit trail" screen (phase 21, `AIA-81`, building on phase 16's read-only API), under System: the whole trail, newest first,
 * paged and filtered on the server (`GET /api/entitlement/audit/entries`), so every entry is reachable however long the trail grows.
 * One search box matches what the actor, the subject or the vendor contains, and it combines with an action chosen from those recorded and a
 * period (the `UR-X-02` dimensions, simplified); every entry opens in a dialog with everything recorded about it, and `UR-X-03`'s chain verification, an occasional check, is a small button whose result
 * opens in a dialog. Gated on `audit.read`. Shows actors and subjects as the record carries them, which an audit trail needs.
 */
export function AuditTrailPage(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <AdminGate permissions={[ReadPermission]} title={t("admin.audit.denied.title")} description={t("admin.audit.denied.description")}>
        <AuditTrailContent />
      </AdminGate>
    </div>
  );
}

export function AuditTrailContent(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<Filters>(NoFilters);
  const [applied, setApplied] = useState<Filters>(NoFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PageSizes[0]);
  const [resetPager, setResetPager] = useState(false);
  const [state, setState] = useState<{ kind: "loading"; previous: AuditEntryPage | null } | { kind: "loaded"; result: AuditEntryPage } | { kind: "error"; message: string }>({
    kind: "loading",
    previous: null,
  });
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [actions, setActions] = useState<string[]>([]);
  const request = useRef(0);

  // The action filter's choices are the actions the trail has recorded. Without them the filter offers only "any action".
  useEffect(() => {
    let cancelled = false;
    void fetchAuditActions().then((result) => {
      if (!cancelled && result.kind === "ok") {
        setActions(result.response);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // The typed filters reach the server once typing pauses; a change of filter goes back to the first page.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setApplied((current) => {
        if (JSON.stringify(current) === JSON.stringify(filters)) {
          return current;
        }
        setPage(1);
        setResetPager((value) => !value);
        return filters;
      });
    }, SearchDelayMs);
    return () => window.clearTimeout(timer);
  }, [filters]);

  const load = useCallback(async () => {
    // Only the latest request's answer is shown, so a slow earlier search cannot overwrite a newer one.
    const id = ++request.current;
    setState((current) => ({ kind: "loading", previous: current.kind === "loaded" ? current.result : current.kind === "loading" ? current.previous : null }));
    const result = await fetchAuditEntries({
      search: applied.search,
      action: applied.action === "" ? undefined : applied.action,
      from: startOfDay(applied.from),
      to: startOfNextDay(applied.to),
      page,
      pageSize,
    });
    if (id !== request.current) {
      return;
    }
    if (result.kind === "ok") {
      setState({ kind: "loaded", result: result.response });
    } else {
      setState({ kind: "error", message: result.kind === "denied" ? t("admin.audit.denied.description") : result.message });
    }
  }, [applied, page, pageSize, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = state.kind === "loaded" ? state.result : state.kind === "loading" ? state.previous : null;
  const hasFilters = Object.values(filters).some((value) => value !== "");
  const systemActor = t("admin.audit.entries.systemActor");
  const actionOptions = useMemo(
    () => [
      { value: "", label: t("admin.audit.filters.anyAction") },
      ...actions.map((action) => ({ value: action, label: actionLabel(action) })).sort((a, b) => a.label.localeCompare(b.label, i18n.language)),
    ],
    [actions, t, i18n.language],
  );

  const columns = useMemo<TableColumn<AuditEntry>[]>(
    () => [
      { id: "sequence", name: t("admin.audit.entries.sequence"), selector: (entry) => entry.sequence, width: "6.5rem", format: (entry) => entry.sequence.toLocaleString(i18n.language) },
      { id: "occurredAt", name: t("admin.audit.entries.occurredAt"), selector: (entry) => entry.occurredAt, format: (entry) => formatDateTime(entry.occurredAt), minWidth: "13.5rem", wrap: true },
      {
        id: "actor",
        name: t("admin.audit.entries.actor"),
        selector: (entry) => actorLabel(entry, systemActor),
        cell: (entry) => (
          <span className={styles.who}>
            <span className={styles.whoName}>{actorLabel(entry, systemActor)}</span>
            <span className={styles.kind}>{entry.actorKind}</span>
          </span>
        ),
        grow: 2,
        wrap: true,
      },
      { id: "action", name: t("admin.audit.entries.action"), selector: (entry) => entry.action, format: (entry) => actionLabel(entry.action), grow: 2, wrap: true },
      { id: "subject", name: t("admin.audit.entries.subject"), selector: (entry) => subjectLabel(entry), grow: 2, wrap: true },
      { id: "vendor", name: t("admin.audit.entries.vendor"), selector: (entry) => entry.subjectVendorId ?? "", width: "8rem" },
      {
        id: "details",
        name: <span className="visually-hidden">{t("dataTable.actionsColumn")}</span>,
        button: true,
        width: "6rem",
        cell: (entry) => (
          <button type="button" className="button button-secondary" aria-label={t("admin.audit.details.open", { sequence: entry.sequence })} onClick={() => setSelected(entry)}>
            {t("admin.audit.details.button")}
          </button>
        ),
      },
    ],
    [t, i18n.language, systemActor],
  );

  return (
    <section className={`${panel.panel} ${styles.panel}`} aria-labelledby="audit-entries-heading">
      <div className={panel.header}>
        <h2 id="audit-entries-heading" className={`panel-title ${panel.title}`}>
          {t("admin.audit.entries.title")}
          {shown !== null && <span className={styles.total}>{t("admin.audit.entries.total", { count: shown.totalCount, formatted: shown.totalCount.toLocaleString(i18n.language) })}</span>}
        </h2>
        <button type="button" className={`button button-secondary ${styles.verify}`} onClick={() => setVerifying(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
          {t("admin.audit.chain.verify")}
        </button>
      </div>

      <div className={styles.filters} role="search" aria-label={t("admin.audit.filters.label")}>
        <FilterField id="audit-filter-search" label={t("admin.audit.filters.search")} placeholder={t("admin.audit.filters.searchPlaceholder")} value={filters.search} onChange={(search) => setFilters({ ...filters, search })} />
        <div className={`${styles.field} ${styles.actionField}`}>
          <label id="audit-filter-action-label" htmlFor="audit-filter-action">
            {t("admin.audit.filters.action")}
          </label>
          <Dropdown id="audit-filter-action" labelledBy="audit-filter-action-label" shape="pill" options={actionOptions} value={filters.action} onChange={(action) => setFilters({ ...filters, action })} />
        </div>
        <FilterField id="audit-filter-from" type="date" label={t("admin.audit.filters.from")} value={filters.from} onChange={(from) => setFilters({ ...filters, from })} />
        <FilterField id="audit-filter-to" type="date" label={t("admin.audit.filters.to")} value={filters.to} onChange={(to) => setFilters({ ...filters, to })} />
        {hasFilters && (
          <button type="button" className={styles.clear} onClick={() => setFilters(NoFilters)}>
            {t("admin.audit.filters.clear")}
          </button>
        )}
      </div>

      {state.kind === "error" ? (
        <ErrorState title={t("admin.audit.entries.errorTitle")} description={state.message} onRetry={() => void load()} />
      ) : (
        <div className={`${adminStyles.tableWrapper} ${adminStyles.peopleTableWrapper} ${styles.table}`} aria-busy={state.kind === "loading"}>
          <DataTable
            columns={columns}
            data={shown?.items ?? []}
            className={adminStyles.peopleDataTable}
            keyField="sequence"
            ariaLabel={t("admin.audit.entries.title")}
            theme={adminTableTheme}
            pagination
            paginationServer
            paginationTotalRows={shown?.totalCount ?? 0}
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={PageSizes}
            paginationResetDefaultPage={resetPager}
            paginationComponent={TablePager}
            onChangePage={(next) => setPage(next)}
            onChangeRowsPerPage={(size, next) => {
              setPageSize(size);
              setPage(next);
            }}
            onRowClicked={(entry) => setSelected(entry)}
            pointerOnHover
            progressPending={state.kind === "loading" && shown === null}
            progressComponent={<LoadingState label={t("admin.audit.entries.loading")} />}
            noDataComponent={<p className={adminStyles.note}>{hasFilters ? t("admin.audit.entries.empty") : t("admin.audit.entries.emptyTrail")}</p>}
            striped
            highlightOnHover
            columnSeparator="subtle"
          />
        </div>
      )}

      {selected !== null && <EntryDetails entry={selected} onClose={() => setSelected(null)} />}
      {verifying && <ChainVerificationDialog onClose={() => setVerifying(false)} />}
    </section>
  );
}

/** One of the search's filters: its label above, a pill-shaped box below. */
function FilterField({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = "search",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: "search" | "date";
}): React.JSX.Element {
  return (
    <div className={type === "date" ? `${styles.field} ${styles.dateField}` : styles.field}>
      <label htmlFor={id}>{label}</label>
      <input id={id} type={type} className={styles.input} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

/**
 * Everything recorded about one entry: what was done, by whom and to what, through which channel, the action's own detail laid out
 * field by field (with the raw JSON a click away), and its place in the hash chain.
 */
function EntryDetails({ entry, onClose }: { entry: AuditEntry; onClose: () => void }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [raw, setRaw] = useState(false);
  const systemActor = t("admin.audit.entries.systemActor");
  const notRecorded = t("admin.audit.details.notRecorded");

  return (
    <Modal title={t("admin.audit.details.title", { sequence: entry.sequence.toLocaleString(i18n.language) })} onClose={onClose} wide lit>
      <div className={styles.details}>
        <p className={styles.headline}>
          <span className={styles.headlineAction}>{actionLabel(entry.action)}</span>
          <span className={styles.headlineWhen}>{formatDateTime(entry.occurredAt)}</span>
        </p>

        <div className={styles.facts}>
          <section className={styles.factGroup} aria-labelledby="audit-details-actor">
            <h3 id="audit-details-actor" className={panel.subheading}>
              {t("admin.audit.details.actor")}
            </h3>
            <dl className={styles.factList}>
              <Fact label={t("admin.audit.details.name")} value={entry.actorName ?? (entry.actorId === null ? systemActor : null)} fallback={notRecorded} />
              <Fact label={t("admin.audit.details.email")} value={entry.actorEmail} fallback={notRecorded} />
              <Fact label={t("admin.audit.details.capacity")} value={entry.actorKind} fallback={notRecorded} />
              <Fact label={t("admin.audit.details.identifier")} value={entry.actorId} fallback={notRecorded} mono />
            </dl>
          </section>

          <section className={styles.factGroup} aria-labelledby="audit-details-subject">
            <h3 id="audit-details-subject" className={panel.subheading}>
              {t("admin.audit.details.subject")}
            </h3>
            <dl className={styles.factList}>
              <Fact label={t("admin.audit.details.kind")} value={entry.subjectKind} fallback={notRecorded} />
              {entry.subjectKind.toLowerCase() === "person" && <Fact label={t("admin.audit.details.name")} value={entry.subjectName} fallback={notRecorded} />}
              {entry.subjectKind.toLowerCase() === "person" && <Fact label={t("admin.audit.details.email")} value={entry.subjectEmail} fallback={notRecorded} />}
              <Fact label={t("admin.audit.details.identifier")} value={entry.subjectId} fallback={notRecorded} mono />
              <Fact label={t("admin.audit.details.vendor")} value={entry.subjectVendorId} fallback={t("admin.audit.details.noVendor")} />
            </dl>
          </section>

          <section className={styles.factGroup} aria-labelledby="audit-details-source">
            <h3 id="audit-details-source" className={panel.subheading}>
              {t("admin.audit.details.source")}
            </h3>
            <dl className={styles.factList}>
              <Fact label={t("admin.audit.details.action")} value={entry.action} fallback={notRecorded} mono />
              <Fact label={t("admin.audit.details.channel")} value={entry.channel} fallback={t("admin.audit.details.website")} />
              <Fact label={t("admin.audit.details.client")} value={entry.clientId} fallback={notRecorded} mono />
              <Fact label={t("admin.audit.details.correlation")} value={entry.correlation} fallback={notRecorded} mono />
            </dl>
          </section>
        </div>

        <section aria-labelledby="audit-details-detail">
          <div className={styles.detailHeader}>
            <h3 id="audit-details-detail" className={panel.subheading}>
              {t("admin.audit.details.detail")}
            </h3>
            {entry.detail !== null && entry.detail !== undefined && (
              <button type="button" className={styles.clear} aria-pressed={raw} onClick={() => setRaw(!raw)}>
                {raw ? t("admin.audit.details.showFields") : t("admin.audit.details.showJson")}
              </button>
            )}
          </div>
          {entry.detail === null || entry.detail === undefined ? (
            <p className={adminStyles.note}>{t("admin.audit.details.noDetail")}</p>
          ) : raw ? (
            <pre className={styles.json}>{JSON.stringify(entry.detail, null, 2)}</pre>
          ) : (
            <div className={styles.detailTree}>
              <DetailValue value={entry.detail} />
            </div>
          )}
        </section>

        <section aria-labelledby="audit-details-chain">
          <h3 id="audit-details-chain" className={panel.subheading}>
            {t("admin.audit.details.chain")}
          </h3>
          <dl className={styles.factList}>
            <Fact label={t("admin.audit.details.entryHash")} value={entry.entryHash} fallback={notRecorded} mono />
            <Fact label={t("admin.audit.details.previousHash")} value={entry.previousHash} fallback={t("admin.audit.details.firstEntry")} mono />
          </dl>
        </section>
      </div>
    </Modal>
  );
}

/** One fact about an entry: its label, then its value, or what stands in for a value that was not recorded. */
function Fact({ label, value, fallback, mono = false }: { label: string; value: string | null | undefined; fallback: string; mono?: boolean }): React.JSX.Element {
  const missing = value === null || value === undefined || value === "";
  return (
    <div className={styles.fact}>
      <dt>{label}</dt>
      <dd className={missing ? styles.missing : mono ? styles.mono : undefined}>{missing ? fallback : value}</dd>
    </div>
  );
}

/** An action's detail, laid out as it is nested: an object as labelled fields, a list as items, a value as itself. */
function DetailValue({ value }: { value: unknown }): React.JSX.Element {
  const { t } = useTranslation();

  if (value === null || value === undefined) {
    return <span className={styles.missing}>{t("admin.audit.details.empty")}</span>;
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? (
      <span className={styles.missing}>{t("admin.audit.details.emptyList")}</span>
    ) : (
      <ol className={styles.detailList}>
        {(value as unknown[]).map((item, index) => (
          <li key={index}>
            <DetailValue value={item} />
          </li>
        ))}
      </ol>
    );
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    return entries.length === 0 ? (
      <span className={styles.missing}>{t("admin.audit.details.empty")}</span>
    ) : (
      <dl className={styles.detailFields}>
        {entries.map(([key, nested]) => (
          <div key={key} className={styles.detailField}>
            <dt>{actionLabel(key)}</dt>
            <dd>
              <DetailValue value={nested} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  if (typeof value === "boolean") {
    return <span>{value ? t("admin.audit.details.yes") : t("admin.audit.details.no")}</span>;
  }
  // What is left of a JSON value is a string or a number.
  return <span className={styles.detailText}>{typeof value === "number" ? value.toString() : typeof value === "string" ? value : JSON.stringify(value)}</span>;
}

/** The chain verification, an occasional check: it runs as the dialog opens and says plainly whether the chain is intact. */
function ChainVerificationDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<{ kind: "verifying" } | { kind: "done"; result: AuditChainVerification } | { kind: "error"; message: string }>({ kind: "verifying" });

  const verify = useCallback(async () => {
    setState({ kind: "verifying" });
    const result = await fetchAuditChainVerification();
    if (result.kind === "ok") {
      setState({ kind: "done", result: result.response });
    } else {
      setState({ kind: "error", message: result.kind === "denied" ? t("admin.audit.denied.description") : result.message });
    }
  }, [t]);

  useEffect(() => {
    void verify();
  }, [verify]);

  return (
    <Modal title={t("admin.audit.chain.title")} onClose={onClose} lit>
      <div className={styles.chain}>
        <p className={adminStyles.note}>{t("admin.audit.chain.explanation")}</p>
        {state.kind === "verifying" && <LoadingState label={t("admin.audit.chain.verifying")} />}
        {state.kind === "error" && <ErrorState title={t("admin.audit.chain.failed")} description={state.message} onRetry={() => void verify()} />}
        {state.kind === "done" && (
          <div className={styles.chainResult} data-intact={state.result.isIntact || undefined} role={state.result.isIntact ? "status" : "alert"}>
            <p className={styles.chainVerdict}>{state.result.isIntact ? t("admin.audit.chain.intactTitle") : t("admin.audit.chain.brokenTitle")}</p>
            <p>
              {state.result.isIntact
                ? t("admin.audit.chain.intact", { count: state.result.entriesChecked, formatted: state.result.entriesChecked.toLocaleString(i18n.language) })
                : t("admin.audit.chain.broken", {
                    sequence: state.result.firstBrokenSequence ?? "?",
                    failure: state.result.failure ?? "",
                    detail: state.result.detail ?? "",
                  })}
            </p>
          </div>
        )}
        {state.kind === "done" && (
          <div className={adminStyles.syncActions}>
            <button type="button" className="button button-secondary" onClick={() => void verify()}>
              {t("admin.audit.chain.again")}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
